import { useEffect, useState } from 'react';
import { Flame } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { BarRow } from './BarRow.js';
import { useHabits } from '../stores/habits.js';
import { listHabitLogs, type HabitLog } from '../api/habits.js';
import { useUi } from '../stores/ui.js';
import { Sheet } from '../ui/Sheet.js';
import { HabitGlyph } from './HabitGlyph.js';
import { teinte } from '../lib/teinte.js';
import { todayInParis, addDays } from '../lib/dates.js';
import {
  weeklyCounts,
  monthlyCounts,
  bestStreak,
  totalDays,
  currentStreak,
} from '../lib/habitStats.js';
import { DEFAULT_HABIT_COLOR, affichee } from '../lib/palette.js';

const WEEKS = 12;
const MONTHS = 6;

function monthLabel(year: number, monthNum: number): string {
  return new Date(Date.UTC(year, monthNum - 1, 1)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    year: 'numeric',
  });
}

export function HabitStatsModal(): React.ReactElement | null {
  const habitId = useUi((s) => s.habitStatsOpenFor);
  const close = useUi((s) => s.closeHabitStats);
  const openHabitForm = useUi((s) => s.openHabitForm);
  const habits = useHabits((s) => s.habits);

  // All-time history, fetched per habit. The store's copy is a rolling window
  // (ADR 0005) — "best" and "total days" computed from it would be wrong, and
  // wrong in the direction that looks plausible.
  // Keyed by habit id: useEffect runs after render, so a plain array would show
  // the previous habit's numbers for a frame when switching from one to another.
  const [fetched, setFetched] = useState<{ hid: string; logs: HabitLog[] } | null>(null);
  const [failed, setFailed] = useState(false);

  const habit = habitId ? habits.find((h) => h.habitId === habitId) : null;

  useEffect(() => {
    if (!habitId) return;
    let cancelled = false;
    setFailed(false);
    listHabitLogs(habitId)
      .then((l) => {
        if (!cancelled) setFetched({ hid: habitId, logs: l });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [habitId]);

  // Guard: store may have dropped the habit (deleted) while the modal was open.
  if (!habit) return null;

  const hid = habit.habitId;
  const today = todayInParis();
  const color = affichee(habit.color ?? DEFAULT_HABIT_COLOR);
  const createdDate = habit.createdAt.slice(0, 10);

  const logs = fetched && fetched.hid === hid ? fetched.logs : null;
  const loaded = logs ?? [];
  const total = totalDays(loaded, hid);
  const weeks = weeklyCounts(loaded, hid, WEEKS, today);
  const months = monthlyCounts(loaded, hid, MONTHS, today);

  return (
    <Sheet
      open
      onClose={close}
      aria-label={`${habit.name} stats`}
      header={
        <div className="flex items-center gap-2 min-w-0">
          <HabitGlyph habit={habit} small />
          <h2 className="text-lg truncate">{habit.name}</h2>
        </div>
      }
      footer={
        <Button
          variant="secondary"
          onClick={() => {
            close();
            openHabitForm(hid);
          }}
        >
          Edit habit
        </Button>
      }
    >
      {/* Never render zeros while the history is in flight or missing: a "best: 0"
          that is really "not loaded" is indistinguishable from the truth. */}
      {failed ? (
        <p className="alerte alerte-erreur" role="alert">
          Historique indisponible.
        </p>
      ) : logs === null ? (
        <div className="pile" aria-busy="true">
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="squelette h-16" />
            ))}
          </div>
          <div className="squelette h-40" />
        </div>
      ) : (
        <div className="pile">
          <div className="grid grid-cols-3 gap-2">
            <Stat value={currentStreak(loaded, hid, today)} label="streak" hero color={color} />
            <Stat value={bestStreak(loaded, hid)} label="best" />
            <Stat value={total} label="total days" />
          </div>

          {total === 0 ? (
            <div className="vide">
              <p>Pas encore d'historique — coche un jour pour démarrer.</p>
            </div>
          ) : (
            <>
              <Section title="Last 12 weeks">
                {weeks.map((w) => (
                  <BarRow
                    key={w.weekStart}
                    label={`W${w.weekNum}`}
                    count={w.count}
                    possible={7}
                    color={color}
                    tracked={addDays(w.weekStart, 6) >= createdDate}
                  />
                ))}
              </Section>
              <Section title="Last 6 months">
                {months.map((m) => (
                  <BarRow
                    key={m.month}
                    label={monthLabel(m.year, m.monthNum)}
                    count={m.count}
                    possible={m.possible}
                    color={color}
                    tracked={m.month >= createdDate.slice(0, 7)}
                  />
                ))}
              </Section>
            </>
          )}
        </div>
      )}
    </Sheet>
  );
}

// `.tuile-centree` du kit : trois chiffres côte à côte.
function Stat({
  value,
  label,
  hero,
  color,
}: {
  value: number;
  label: string;
  hero?: boolean;
  color?: string;
}): React.ReactElement {
  return (
    <div className="tuile tuile-centree">
      {hero && (
        <span className="tuile-icone" style={teinte(color, '--tuile-teinte')}>
          <Flame size={16} aria-hidden />
        </span>
      )}
      <span className="tuile-valeur">{value}</span>
      <span className="tuile-libelle">{label}</span>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="pile pile-serree">
      <h3 className="mention">{title}</h3>
      {children}
    </section>
  );
}
